import { Box, makeStyles, styled } from "@material-ui/core";
import { memo } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_INACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import { DragonflyMark } from "../dragonfly-mark";
import { RichText } from "./rich-text";

const Turn = styled(Box)({
  display: "flex",
  gap: 9,
  alignItems: "flex-start",
});

const Avatar = styled(Box)({
  width: 28,
  height: 28,
  borderRadius: "50%",
  flex: "none",
  backgroundColor: EPOCUS_PRIMARY,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Bubble = styled("div")({
  backgroundColor: EPOCUS_SELECTED_BG,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 14,
  borderTopLeftRadius: 4,
  padding: "11px 14px",
  fontSize: rem(14),
  lineHeight: 1.5,
  color: EPOCUS_TEXT_PRIMARY,
  maxWidth: "82%",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

const WARM = {
  backgroundColor: EPOCUS_SELECTED_BG,
  borderColor: EPOCUS_PRIMARY,
} as const;

const UserTurn = styled(Box)({ display: "flex", justifyContent: "flex-end" });

const UserBubble = styled("div")({
  backgroundColor: EPOCUS_TEXT_PRIMARY,
  color: EPOCUS_WHITE,
  borderRadius: 14,
  borderTopRightRadius: 4,
  padding: "9px 13px",
  fontSize: rem(13.5),
  fontWeight: 500,
  maxWidth: "82%",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

// MUI v4 `styled()` compiles a single rule; a nested @keyframes throws in JSS.
const useTypingStyles = makeStyles({
  "@keyframes blink": {
    "0%, 60%, 100%": { opacity: 0.3 },
    "30%": { opacity: 1 },
  },
  row: { display: "flex", gap: 4, padding: "3px 0" },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: EPOCUS_INACTIVE,
    animation: "$blink 1.2s infinite",
    "&:nth-child(2)": { animationDelay: ".2s" },
    "&:nth-child(3)": { animationDelay: ".4s" },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
});

export const AssistantMessage = memo<{ text: string; warm?: boolean }>(
  ({ text, warm }) => (
    <Turn>
      <Avatar>
        <DragonflyMark size={17} mono />
      </Avatar>
      <Bubble style={warm ? WARM : undefined}>
        <RichText text={text} />
      </Bubble>
    </Turn>
  ),
);
AssistantMessage.displayName = "AssistantMessage";

export const TypingIndicator: FC = () => {
  const classes = useTypingStyles();
  return (
    <Turn aria-label="Dragonfly is typing">
      <Avatar>
        <DragonflyMark size={17} mono />
      </Avatar>
      <Bubble>
        <div className={classes.row}>
          <span className={classes.dot} />
          <span className={classes.dot} />
          <span className={classes.dot} />
        </div>
      </Bubble>
    </Turn>
  );
};

export const UserMessage = memo<{ text: string }>(({ text }) => (
  <UserTurn>
    <UserBubble>{text}</UserBubble>
  </UserTurn>
));
UserMessage.displayName = "UserMessage";
